import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import AboutStatsSection from "./components/StatsSection";
import ReputationSection from "./components/ReputationSection";

const SUBMIT_URL = "https://readdy.ai/api/form/d9qr32ue3oq4jqumtpb0";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const organizationSchema = {
    name: "海角千万资源原创",
    url: "https://www.haijiao-resource.com",
    description: "专注于海量原创内容资源的聚合平台，涵盖图文、视频、创意素材等多元类型。",
    address: {
      "@type": "PostalAddress",
      addressCountry: "CN",
    },
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const hp = (form.querySelector('[name="mobile_alt"]') as HTMLInputElement)?.value?.trim();
    if (hp) {
      setFormStatus("success");
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const formData = new FormData(form);
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后重试";
        if (msg.toLowerCase().includes("spam") || msg.includes("form data is spam")) {
          setFormStatus("success");
        } else {
          setFormError(msg);
          setFormStatus("error");
        }
      }
    } catch {
      setFormError("网络错误，请稍后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" data={organizationSchema as Record<string, unknown>} />
      <SchemaOrg type="WebPage" data={{ name: "关于我们 - 海角千万资源原创", url: "https://www.haijiao-resource.com/about" }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [{ "@type": "ListItem", position: 1, name: "首页", item: "https://www.haijiao-resource.com/" }, { "@type": "ListItem", position: 2, name: "关于我们" }] }} />

      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-4">
              <Breadcrumb items={[{ label: "关于我们" }]} />
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">关于我们</h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
              海角千万资源原创——正版内容聚合平台，致力于为创作者与企业提供高质量、可信赖的正版原创资源。
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-primary-500 text-sm font-medium mb-3">
                  <i className="ri-building-2-line"></i>
                  <span>品牌介绍</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  海角千万资源原创——正版内容聚合平台
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                  <p>
                    <strong>海角千万资源原创</strong>成立于2023年，专注于海量原创内容资源的聚合与分发。我们深刻理解创作者在内容生产过程中对高质量素材的需求，以及对版权合规性的担忧。
                  </p>
                  <p>
                    平台涵盖<strong>图文素材、原创视频、创意素材、设计模板</strong>等多元资源类型，汇聚了超过<strong>一千万件</strong>正版原创内容。无论是个人创作者的自媒体运营，还是企业品牌的整合营销传播，海角千万都能提供全面、可靠的内容资源支持。
                  </p>
                  <p>
                    我们始终坚持<strong>正版原创</strong>的核心理念，与超过五千位认证创作者建立了长期合作关系。通过严格的原创审核机制和完善的版权溯源体系，确保每一件上架资源都具备明确的版权归属和清晰的授权范围。
                  </p>
                  <p>
                    截至目前，海角千万资源原创已服务超过<strong>两百万家企业客户</strong>，覆盖科技、金融、教育、消费、文化等多个行业领域。我们的使命是让每一位创作者都能高效便捷地获取正版原创资源，让创作回归创意本身。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden h-80 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20open%20workspace%20layout%2C%20designers%20collaborating%20around%20large%20screens%20showing%20creative%20content%2C%20warm%20natural%20lighting%20from%20floor%20to%20ceiling%20windows%2C%20plants%20and%20art%20on%20walls%2C%20professional%20yet%20warm%20atmosphere%2C%20editorial%20corporate%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="海角千万资源原创团队办公环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Platform Features */}
        <section className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-accent-500 text-sm font-medium mb-3">
                <i className="ri-star-line"></i>
                <span>核心优势</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                为什么选择海角千万
              </h2>
              <p className="text-foreground-600 text-sm max-w-xl mx-auto">
                我们以正版原创为核心，为创作者和企业提供全方位的资源服务
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-shield-star-line",
                  title: "正版保障",
                  desc: "所有资源均经过严格的原创审核和版权溯源，确保每件素材都具备清晰的授权范围，创作者可放心使用。",
                  color: "primary"
                },
                {
                  icon: "ri-stack-line",
                  title: "海量资源",
                  desc: "千万级原创内容资源库持续更新，每日新增超过500件优质素材，满足不同创作场景的多样化需求。",
                  color: "accent"
                },
                {
                  icon: "ri-flashlight-line",
                  title: "高效获取",
                  desc: "智能搜索和分类推荐系统，帮助创作者快速定位所需资源，大幅缩短素材寻找时间，提升创作效率。",
                  color: "secondary"
                },
                {
                  icon: "ri-team-line",
                  title: "创作者生态",
                  desc: "汇聚超过五千位专业认证创作者，建立健康可持续的内容创作生态，促进创作者与企业的高效对接。",
                  color: "accent"
                },
                {
                  icon: "ri-price-tag-3-line",
                  title: "灵活授权",
                  desc: "提供免费、付费和企业定制等多种授权模式，根据用户的实际需求灵活选择，降低内容采购成本。",
                  color: "primary"
                },
                {
                  icon: "ri-customer-service-2-line",
                  title: "专业服务",
                  desc: "专属客户支持团队提供7×24小时服务，版权争议24小时响应，确保用户使用无忧。",
                  color: "secondary"
                },
              ].map((feature, i) => (
                <div key={i} className="p-6 rounded-2xl bg-background-50 border border-background-200 hover:border-primary-200 transition-colors duration-200">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${
                    feature.color === "primary" ? "bg-primary-100 text-primary-500"
                    : feature.color === "accent" ? "bg-accent-100 text-accent-500"
                    : "bg-secondary-100 text-secondary-500"
                  }`}>
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <AboutStatsSection />
          </div>
        </section>

        {/* Reputation */}
        <section className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <ReputationSection />
          </div>
        </section>

        {/* Contact & FAQ */}
        <section className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <div className="inline-flex items-center gap-2 text-primary-500 text-sm font-medium mb-3">
                  <i className="ri-mail-send-line"></i>
                  <span>联系我们</span>
                </div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">商务合作与咨询</h2>
                <p className="text-foreground-600 text-sm mb-6">
                  如果您有任何问题或合作意向，欢迎通过以下表单与我们联系
                </p>

                <form
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="mobile_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">公司/机构</label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      placeholder="请输入您的公司或机构名称"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">咨询类型</label>
                    <select
                      id="contact-subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    >
                      <option value="">请选择咨询类型</option>
                      <option value="企业合作">企业资源采购合作</option>
                      <option value="创作者入驻">创作者认证入驻</option>
                      <option value="版权咨询">版权合规与授权咨询</option>
                      <option value="技术支持">技术支持与反馈</option>
                      <option value="其他">其他问题</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap"
                  >
                    {formStatus === "submitting" ? "提交中..." : "发送留言"}
                  </button>

                  {formStatus === "success" && (
                    <p className="text-accent-600 text-sm text-center">您的留言已成功提交，我们将尽快与您联系！</p>
                  )}
                  {formStatus === "error" && formError && (
                    <p className="text-red-500 text-sm text-center">{formError}</p>
                  )}
                </form>
              </div>

              {/* Contact Info */}
              <div>
                <div className="inline-flex items-center gap-2 text-accent-500 text-sm font-medium mb-3">
                  <i className="ri-map-pin-line"></i>
                  <span>联系方式</span>
                </div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">随时与我们沟通</h2>

                <div className="space-y-5">
                  {[
                    { icon: "ri-mail-line", label: "电子邮箱", value: "contact@haijiao-resource.com" },
                    { icon: "ri-phone-line", label: "客服热线", value: "400-888-9999" },
                    { icon: "ri-time-line", label: "工作时间", value: "周一至周五 9:00 - 18:00" },
                    { icon: "ri-map-pin-2-line", label: "办公地址", value: "中国广东省深圳市南山区科技园" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 flex-shrink-0">
                        <i className={`${item.icon} text-secondary-500`}></i>
                      </div>
                      <div>
                        <div className="text-foreground-500 text-xs mb-0.5">{item.label}</div>
                        <div className="text-foreground-900 text-sm font-medium">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-5 rounded-2xl bg-background-50 border border-background-200">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">关于海角原创资源下载</h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">
                    海角千万资源原创平台的所有资源均支持在线预览和下载。个人用户可通过注册账号免费获取有限数量的素材；企业用户可开通企业版服务，享受无限制下载和专属定制服务。具体使用条款请参阅服务协议。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}